import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepsService } from '../../../services/steps.service';
import { Step } from '../../stepper/step';
import { ComponentWithErrors } from '../../../classes/component-with-errors';
import { AppSettings } from '../../../app.settings';
import { IInitializePasswordRequest, InitializePasswordRequest, InitializePassword } from '../../../classes/initialize-password';
import { ViewChild } from '@angular/core';
import { RestorePasswordWithCardContainerComponent } from './restore-password-with-card-container.component';
import { IUserData, UserData } from '../../../../globals/classes/userData';
import { PasswordValidator } from '../../../../globals/classes/password-validator';
import { ValidationService } from '../../../../globals/classes/validaton-service';
import { ICodeService } from '../../../services/icode.service';
import { GoogleAnalyticsUtils } from '../../../classes/static/google-analytics-utils';
import { IUserIDRequest } from '../../../classes/http-requests';
import { ISendSmsOtpResponse } from '../../../classes/http-responses';
import { finalize } from 'rxjs/operators/finalize';
import { ICardLoginResponse } from '../../../classes/existing-onboarding-customer/responses';

@Component({
    selector: 'restore-password',
    template: require('./restore-password-with-card.component.html'),
})
export class RestorePasswordWithCardComponent extends ComponentWithErrors implements OnInit {
    public steps: Step[];
    public loading: boolean = false;
    public isRebrading = AppSettings.isRebrading;
    public isLoginBigModalEnabled: boolean = AppSettings.isLoginBigModalEnabled;
    public initRequest: IInitializePasswordRequest = new InitializePasswordRequest();
    public hasBack: boolean = true;
    public isValidForm: boolean = false;
    private userData: IUserData = new UserData();
    public icode: string;
    private arePasswordsValid: boolean = false;

    // Flags for card verification step
    public isCardInputValid: boolean = false;
    public cardVerified: boolean = false;

    @ViewChild(RestorePasswordWithCardContainerComponent, { static: false }) containerComponent: RestorePasswordWithCardContainerComponent;

    constructor(
        public stepsService: StepsService,
        private activeModal: NgbActiveModal,
        private icodeService: ICodeService
    ) {
        super();
    }

    ngOnInit() {
        this.steps = this.stepsService.getTransactionSteps('RestorePasswordWithCard');
        this.stepsService.setStep(this.steps, 0);

        this.initRequest.passwordNew = '';
        this.initRequest.passwordConfirmNew = '';

        this.initializeSubscriptions();

        ValidationService.GlobalValidation.resetValidation();

        this.mortalize(UserData.data$).subscribe(userData => {
            if (!userData) return;
            this.userData = userData;
            this.initRequest.userID = this.userData.userId;
        });

        // Track card details entry screen (Step 0)
        this.setDynatraceEvent('IB_PR_Enter_Card_Details');
    }

    public closeModal(): void {
        this.activeModal.close();
    }

    public moveBackStep(steps: Step[]): void {
        if (this.stepsService.getCurrentStep === 0) {
            this.closeModal();
        } else {
            this.stepsService.moveBackStep(steps);
        }
    }

    public moveStep(): void {
        switch (this.stepsService.getCurrentStep) {
            case 0:
                // Start card verification process
                if (this.containerComponent && this.containerComponent.cardVerificationComponent) {
                    this.containerComponent.cardVerificationComponent.continue();
                    // The step transition will happen after verification completes
                }
                break;
            case 1:
                this.sendInitPass();
                break;
            case 2:
                this.initializePassword();
                break;
            case 3:
                this.closeModal();
                break;
            default:
                this.handleError();
        }
    }

    public onCardValid(isValid: boolean): void {
        this.isCardInputValid = isValid;
    }

    public onCardVerified(response: ICardLoginResponse): void {
        this.cardVerified = true;

        // Immediately move to the next step after verification
        this.stepsService.moveNextStep(this.steps);
        
        // Track password creation screen (Step 1)
        this.setDynatraceEvent('IB_PR_Card_Create_New_Password');
    }

    public sendInitPass(moveNextStep = true) {
        this.loading = true;
        let request: IUserIDRequest = { UserID: this.initRequest.userID };
        InitializePassword.SendInitOtpPass(request)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(
                (res) => {
                    if (res && res.succeeded === true) {
                        this.resolveSendInitPassResponse(request, res, moveNextStep);
                    } else {
                        this.handleError();
                    }
                },
                (error) => {
                    this.handleError(error);
                }
            );
    }

    private resolveSendInitPassResponse(
        request: IUserIDRequest,
        res: ISendSmsOtpResponse,
        moveNextStep = true
    ) {
        this.icodeService.resetTimer();
        this.userData.userId = request.UserID;
        this.userData.hasSMSOtp = res.hasOtp;
        this.userData.maskedPhoneNumber = res.maskedPhoneNumber;
        UserData.update(this.userData);
        if (moveNextStep) {
            this.stepsService.moveNextStep(this.steps);
            // Track OTP confirmation screen (Step 2)
            this.setDynatraceEvent('IB_PR_Card_Confirm_OTP');
        }
        GoogleAnalyticsUtils.sendRetrievePasswordStep4();
    }

    public initializePassword() {
        this.loading = true;
        this.initRequest.otp = this.icode;
        this.initRequest.userID = this.userData.userId;
        InitializePassword.initializePassword(this.initRequest)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(
                (res) => {
                    if (res && res.succeeded) {
                        this.stepsService.moveNextStep(this.steps);
                        GoogleAnalyticsUtils.sendRetrievePasswordStep5();
                        // Track success screen (Step 3)
                        this.setDynatraceEvent('IB_PR_Card_Success');
                    } else {
                        this.handleError();
                    }
                },
                (error) => {
                    this.handleError(error);
                }
            );
    }

    public get validFields(): boolean {
        switch (this.stepsService.getCurrentStep) {
            case 0:
                // Card verification step
                return this.cardVerified || this.isCardInputValid;
            case 1:
                // Password creation step
                return this.arePasswordsValid;
            case 2:
                // OTP verification step
                return this.isIcodeValid;
            default:
                return true;
        }
    }

    private get isIcodeValid(): boolean {
        return this.icodeService.isValidCode(this.icode);
    }

    private initializeSubscriptions() {
        this.mortalize(this.icodeService.code$).subscribe((c) => (this.icode = c));
        this.mortalize(this.icodeService.codeRequested$).subscribe(() => this.sendInitPass(false));
        this.mortalize(PasswordValidator.isValidPassAndConfirm$).subscribe(
            (val) => (this.arePasswordsValid = val)
        );
    }
}
